import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  useTheme,
  Tooltip,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import StrategyIcon from "@mui/icons-material/TrendingUp";
import moment from "moment";
import { useSelector } from "react-redux";
import { selectManagers } from "../../Redux/Reducers/selectors";

const StrategiesTable = ({ strategies, onEdit, onDelete, onAdd }) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(100);

  const managers = useSelector(selectManagers);
  const columns = [
    {
      field: "name",
      headerName: "Strategy Name",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <StrategyIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
          <Typography
            sx={{
              fontWeight: 500,
              color: theme.palette.text.primary,
            }}
          >
            {params.value || "-"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "owner",
      headerName: "Owner",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const owner = params.value;
        let ownerName = owner;
        
        if (owner?.includes('user#')) {
          ownerName = owner.split('#')[1];
        } else if (owner?.includes('manager#')) {
          const managerId = owner.split('#')[1];
          const manager = managers?.find(m => m?.managerId === managerId);
          ownerName = manager?.name || 'Manager';
        }
        
        return (
          <Typography
            sx={{
              fontWeight: 500,
              color: theme.palette.text.primary,
            }}
          >
            {ownerName || "-"}
          </Typography>
        );
      },
    },
    {
      field: "permissions",
      headerName: "Permissions",
      width: 130,
      renderCell: (params) => (
        <Typography
          sx={{
            fontWeight: 500,
            color: theme.palette.text.primary,
          }}
        >
          {params.row.permissions?.default ? "List" : "List"}
        </Typography>
      ),
    },
    {
      field: "date",
      headerName: "Last Updated",
      width: 150,
      renderCell: (params) => (
        <Typography
          sx={{
            fontWeight: 500,
            color: theme.palette.text.primary,
          }}
        >
          {params.value ? moment(params.value).format("MMM DD, YYYY") : "-"}
        </Typography>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography
          sx={{
            fontWeight: 500,
            color: theme.palette.text.primary,
          }}
        >
          {params.value || "-"}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton 
              color="primary" 
              size="small" 
              onClick={() => onEdit(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton 
              color="error" 
              size="small" 
              onClick={() => onDelete(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const filteredData = useMemo(() => {
    let data = strategies || [];
    
    // Apply search filter
    if (searchTerm) {
      data = data.filter((strategy) =>
        strategy.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        strategy.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        strategy.owner?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Sort by name in ascending order (default behavior)
    data = [...data].sort((a, b) => {
      const aValue = a.name || '';
      const bValue = b.name || '';
      return aValue.localeCompare(bValue);
    });
    
    return data;
  }, [strategies, searchTerm]);

  // Add id field to each row for DataGrid
  const rowsWithId = useMemo(() => {
    return filteredData.map((strategy) => ({
      ...strategy,
      id: strategy.strategyId || Math.random().toString(),
    }));
  }, [filteredData]);

  return (
    <Box >
      {/* Header with Search and Add Button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
        }}
      >
        <TextField
          placeholder="Search strategies..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: theme.palette.text.secondary }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: 300,
            "& .MuiOutlinedInput-root": {
              height: 40,
            },
          }}
        />
        
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAdd}
          sx={{
            borderRadius: 1.5,
            textTransform: "none",
            fontWeight: 600,
            minWidth: 120,
          }}
        >
          Add Strategy
        </Button>
      </Box>

      {/* DataGrid */}
      <Box >
        <DataGridPro
          rows={rowsWithId}
          columns={columns}
          pageSize={1000}
          onPageSizeChange={setPageSize}
          rowsPerPageOptions={[25, 50, 100, 500, 1000, 2000]}
          pagination
          density="compact"
          disableSelectionOnClick
          sx={{
            border: "none",
            height: "calc(100vh - 20px)",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: theme.palette.grey[100],
              "& .MuiDataGrid-columnHeader": {
                fontWeight: 600,
                color: theme.palette.text.primary,
              },
            },
            "& .MuiDataGrid-cell": {
              borderBottom: `1px solid ${theme.palette.divider}`,
              color: theme.palette.text.primary,
            },
            "& .MuiDataGrid-row": {
              "&:hover": {
                backgroundColor: theme.palette.grey[50],
              },
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: `1px solid ${theme.palette.divider}`,
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default StrategiesTable;